import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe2,
  Search,
  ExternalLink,
  MessageCircle,
  ArrowUpRight,
  Sparkles,
  Lock,
  Dribbble,
  Waves,
  Bike,
  Footprints,
  Trophy,
  Target,
  CircleDot,
  Smartphone,
  KeyRound,
  ArrowRightLeft,
  Store,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Section from '../components/Section';
import TaekwondoIcon from '../components/icons/TaekwondoIcon';
import { SF_UI } from '../components/ui/tokens';
import oneTaekwondoBackdrop from '../assets/oneworld/taekwondo/onetaekwondo_backdrop.jpg';

/** External One Taekwondo app (under construction) */
export const ONE_TAEKWONDO_APP_URL = 'https://onetaekwondo.netlify.app/';

/** Host WhatsApp support for One Taekwondo / ONE series */
export const ONE_WORLD_WHATSAPP_URL = 'https://wa.me/59996703540';

export const ONE_WORLD_HEADER =
  'One World is the central hub for sports in the SportsFuture ecosystem — pick a world, enter the sport you live for.';

export const ONE_WORLD_SUBTITLE =
  'Every discipline becomes its own world: OneTaekwondo, OneFootball, and more. Find yours and step in.';

/** OpenPlaza Partner Pass — the on-chain key for a sport world to mint, label, and sell */
export const OPPP_CONTRACT = '0x6AD2cc38b1c1f377E066BA7d8812c8373ed2Fa20';

export const ONE_WORLD_LINKS = {
  oneTaekwondo: ONE_TAEKWONDO_APP_URL,
  openPlaza: 'https://openplaza.io',
  opppCollection: `https://openplaza.io/nft/${OPPP_CONTRACT}`,
  classics: '/legacy/classics',
} as const;

export const ONE_WORLD_START = [
  {
    step: 1,
    title: 'Enter the sport diAPP',
    body: 'Each world is a familiar web2 sport home — clubs, athletes, and fans. OneTaekwondo is the first live diAPP; more worlds open next.',
    icon: Smartphone,
    ctaLabel: 'Open OneTaekwondo',
    href: ONE_WORLD_LINKS.oneTaekwondo,
    external: true,
  },
  {
    step: 2,
    title: 'Get an OpenPlaza Partner Pass (OPPP)',
    body: 'OPPP is the on-chain key for a sport world to mint editions, attach labels, and run partner sales on OpenPlaza.',
    icon: KeyRound,
    ctaLabel: 'View OPPP on OpenPlaza',
    href: ONE_WORLD_LINKS.opppCollection,
    external: true,
  },
  {
    step: 3,
    title: 'Add web2 to web3',
    body: 'Members stay in the sport app they already know. Ownership, editions, and legacy move on-chain on Apertum via OpenPlaza.',
    icon: ArrowRightLeft,
    ctaLabel: 'OpenPlaza marketplace',
    href: ONE_WORLD_LINKS.openPlaza,
    external: true,
  },
  {
    step: 4,
    title: 'Mint and share on Classics',
    body: 'Wallet, APTM, KYC, then mint a unique NFT or edition. Classics is the practical on-chain entry for every world.',
    icon: Store,
    ctaLabel: 'Open Classics path',
    href: ONE_WORLD_LINKS.classics,
    external: false,
  },
] as const;

export type WorldStatus = 'live' | 'soon';

export type OneWorld = {
  id: string;
  /** Display name of the sport */
  name: string;
  /** ONE naming: One + SportsName, e.g. OneTaekwondo */
  worldName: string;
  /** Compact label e.g. ONETAEKWONDO */
  seriesLabel: string;
  tagline: string;
  status: WorldStatus;
  /** External app when live */
  href?: string;
  /** Optional image from /public */
  image?: string;
  /** Tailwind gradient classes for card accent */
  gradient: string;
  accent: string;
  icon: LucideIcon;
  category: 'combat' | 'team' | 'endurance' | 'racquet' | 'para' | 'other';
};

/**
 * Worlds registry — each connected sport is One{SportsName}.
 * Only Taekwondo is live on SportsFuture today; others frame the hub as multi-sport.
 */
export const ONE_WORLDS: OneWorld[] = [
  {
    id: 'taekwondo',
    name: 'Taekwondo',
    worldName: 'OneTaekwondo',
    seriesLabel: 'ONETAEKWONDO',
    tagline: 'The first live world — legacy, clubs, and the path to your next Black Belt.',
    status: 'live',
    href: ONE_TAEKWONDO_APP_URL,
    image: oneTaekwondoBackdrop,
    gradient: 'from-red-600 via-rose-500 to-amber-500',
    accent: 'bg-rose-500',
    icon: TaekwondoIcon,
    category: 'combat',
  },
  {
    id: 'football',
    name: 'Football',
    worldName: 'OneFootball',
    seriesLabel: 'ONEFOOTBALL',
    tagline: 'Pitch to legacy — clubs, players, and global game culture.',
    status: 'soon',
    image: '/pexels_football.jpg',
    gradient: 'from-emerald-700 via-green-600 to-lime-500',
    accent: 'bg-emerald-500',
    icon: CircleDot,
    category: 'team',
  },
  {
    id: 'basketball',
    name: 'Basketball',
    worldName: 'OneBasketball',
    seriesLabel: 'ONEBASKETBALL',
    tagline: 'Court energy, team identity, and highlight moments.',
    status: 'soon',
    image: '/pexels_female_basketball.jpg',
    gradient: 'from-orange-600 via-amber-500 to-yellow-400',
    accent: 'bg-orange-500',
    icon: Dribbble,
    category: 'team',
  },
  {
    id: 'swimming',
    name: 'Swimming',
    worldName: 'OneSwimming',
    seriesLabel: 'ONESWIMMING',
    tagline: 'Lanes, times, and the quiet power of the water.',
    status: 'soon',
    image: '/swimming.png',
    gradient: 'from-cyan-700 via-sky-500 to-blue-400',
    accent: 'bg-sky-500',
    icon: Waves,
    category: 'endurance',
  },
  {
    id: 'cycling',
    name: 'Cycling',
    worldName: 'OneCycling',
    seriesLabel: 'ONECYCLING',
    tagline: 'Roads, tracks, and the long ride of legacy.',
    status: 'soon',
    image: '/pexels_cycling.jpg',
    gradient: 'from-yellow-600 via-lime-500 to-emerald-400',
    accent: 'bg-lime-500',
    icon: Bike,
    category: 'endurance',
  },
  {
    id: 'athletics',
    name: 'Athletics',
    worldName: 'OneAthletics',
    seriesLabel: 'ONEATHLETICS',
    tagline: 'Run, jump, throw — pure performance, pure story.',
    status: 'soon',
    image: '/pexels_runners.jpg',
    gradient: 'from-violet-700 via-purple-500 to-fuchsia-400',
    accent: 'bg-violet-500',
    icon: Footprints,
    category: 'endurance',
  },
  {
    id: 'rugby',
    name: 'Rugby',
    worldName: 'OneRugby',
    seriesLabel: 'ONERUGBY',
    tagline: 'Contact, courage, and club brotherhood.',
    status: 'soon',
    image: '/pexels_rugby.jpg',
    gradient: 'from-green-800 via-teal-600 to-cyan-500',
    accent: 'bg-teal-600',
    icon: Trophy,
    category: 'team',
  },
  {
    id: 'badminton',
    name: 'Badminton',
    worldName: 'OneBadminton',
    seriesLabel: 'ONEBADMINTON',
    tagline: 'Speed, precision, and the rally that defines you.',
    status: 'soon',
    image: '/badmi.png',
    gradient: 'from-indigo-700 via-blue-500 to-cyan-400',
    accent: 'bg-indigo-500',
    icon: Target,
    category: 'racquet',
  },
];

const CATEGORY_FILTERS: { id: 'all' | OneWorld['category']; label: string }[] = [
  { id: 'all', label: 'All worlds' },
  { id: 'combat', label: 'Combat' },
  { id: 'team', label: 'Team' },
  { id: 'endurance', label: 'Endurance' },
  { id: 'racquet', label: 'Racquet' },
];

function WorldCard({ world, index }: { world: OneWorld; index: number }) {
  const Icon = world.icon;
  const isLive = world.status === 'live';
  const Wrapper = isLive && world.href ? 'a' : 'div';
  const wrapperProps =
    isLive && world.href
      ? {
          href: world.href,
          target: '_blank' as const,
          rel: 'noopener noreferrer',
        }
      : {};

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.35) }}
      className="h-full"
    >
      <Wrapper
        {...wrapperProps}
        className={`${SF_UI.systemId} group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-md transition-all duration-300 ${
          isLive
            ? 'border-transparent ring-2 ring-primary/30 hover:-translate-y-1.5 hover:shadow-xl hover:ring-primary/60 cursor-pointer'
            : 'border-gray-100 opacity-90 cursor-default'
        }`}
        aria-label={
          isLive
            ? `Enter ${world.worldName} world`
            : `${world.worldName} — coming soon`
        }
      >
        {/* Visual header */}
        <div className={`relative h-40 overflow-hidden bg-gradient-to-br ${world.gradient}`}>
          {world.image && (
            <img
              src={world.image}
              alt=""
              className={`absolute inset-0 h-full w-full object-cover transition-transform duration-500 ${
                isLive ? 'group-hover:scale-110' : 'opacity-50 grayscale-[30%]'
              }`}
            />
          )}
          <div
            className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent ${
              world.image ? '' : ''
            }`}
          />

          {/* Floating icon orb */}
          <div className="absolute left-4 top-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 backdrop-blur-md border border-white/25 text-white shadow-lg">
            <Icon size={24} aria-hidden />
          </div>

          {/* Status badge */}
          <div className="absolute right-4 top-4">
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary shadow-sm">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                </span>
                Live
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/90 backdrop-blur-sm border border-white/15">
                <Lock size={10} aria-hidden />
                Soon
              </span>
            )}
          </div>

          <div className="absolute bottom-3 left-4 right-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">
              {world.seriesLabel}
            </p>
            <h3 className="text-2xl font-bold text-white drop-shadow-sm">{world.worldName}</h3>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col p-5">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
            {world.name}
          </p>
          <p className="flex-1 text-sm leading-relaxed text-gray-600">{world.tagline}</p>

          {isLive ? (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors group-hover:bg-primary-600">
                Enter world
                <ArrowUpRight size={16} className="ml-1.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
              <span className="text-xs text-gray-400">Opens OneTaekwondo app</span>
            </div>
          ) : (
            <div className="mt-5 flex items-center text-sm font-medium text-gray-400">
              <Sparkles size={14} className="mr-1.5 opacity-60" />
              World opening soon
            </div>
          )}
        </div>

        {/* Live shimmer edge */}
        {isLive && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-80"
            aria-hidden
          />
        )}
      </Wrapper>
    </motion.div>
  );
}

const OneWorldPage = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<(typeof CATEGORY_FILTERS)[number]['id']>('all');

  const liveCount = ONE_WORLDS.filter((w) => w.status === 'live').length;
  const soonCount = ONE_WORLDS.filter((w) => w.status === 'soon').length;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ONE_WORLDS.filter((w) => {
      const catOk = category === 'all' || w.category === category;
      if (!catOk) return false;
      if (!q) return true;
      return (
        w.name.toLowerCase().includes(q) ||
        w.worldName.toLowerCase().includes(q) ||
        w.seriesLabel.toLowerCase().includes(q) ||
        w.tagline.toLowerCase().includes(q)
      );
    }).sort((a, b) => {
      // Live worlds first, then alpha by worldName
      if (a.status !== b.status) return a.status === 'live' ? -1 : 1;
      return a.worldName.localeCompare(b.worldName);
    });
  }, [query, category]);

  return (
    <>
      {/* Immersive hub hero — shared SF_UI language */}
      <section
        className={`${SF_UI.systemId} ${SF_UI.heroShell}`}
        data-sf-ui="page-hero"
      >
        <div className={SF_UI.heroOrbPrimary} aria-hidden />
        <div className={SF_UI.heroOrbSecondary} aria-hidden />
        <div className={SF_UI.heroOrbSoft} aria-hidden />

        <div className="container relative z-10 mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="mx-auto max-w-3xl text-center"
          >
            <p className={`mb-3 inline-flex items-center gap-2 ${SF_UI.microLabel}`}>
              Legacy · One World
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full uppercase tracking-wide">
                New
              </span>
            </p>
            <div className="mb-5 flex items-center justify-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur border border-white/15 shadow-lg shadow-primary/20">
                <Globe2 size={30} className="text-primary-100" aria-hidden />
              </span>
            </div>
            <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-6xl">
              Find your{' '}
              <span className="bg-gradient-to-r from-primary-200 via-white to-secondary-200 bg-clip-text text-transparent">
                world
              </span>
            </h1>
            <p className="mx-auto mb-3 max-w-2xl text-lg text-slate-200 md:text-xl">
              {ONE_WORLD_HEADER}
            </p>
            <p className="mx-auto mb-8 max-w-xl text-sm text-slate-400 md:text-base">
              {ONE_WORLD_SUBTITLE}
            </p>

            {/* Search */}
            <div className="relative mx-auto max-w-xl">
              <label htmlFor="one-world-search" className="sr-only">
                Search sports worlds
              </label>
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                id="one-world-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search worlds — e.g. Taekwondo, OneFootball…"
                className="w-full rounded-2xl border border-white/15 bg-white/10 py-3.5 pl-11 pr-4 text-white placeholder:text-slate-400 shadow-inner backdrop-blur-md outline-none ring-primary/40 transition focus:bg-white/15 focus:ring-2"
                autoComplete="off"
              />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {liveCount} live
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                <Lock size={10} />
                {soonCount} opening soon
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1">
                Naming: One + Sport
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to start — diAPP + OPPP + web2→web3 */}
      <Section bgColor="bg-white" className="pt-12 pb-4">
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mb-10 text-center"
          >
            <p className={`mb-2 ${SF_UI.microLabelOnLight}`}>How to start</p>
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
              diAPP + OPPP: web2 sports on web3
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm font-medium text-gray-500">
              diAPP — decentralized independent Apertum Partner Program. A play on dAPP
              (decentralized application).
            </p>
            <p className="mx-auto mt-3 max-w-2xl text-gray-600">
              One World is how SportsFuture adds existing sports — clubs, federations, and sport
              apps — to the Apertum chain. The sport diAPP stays the front door. OpenPlaza Partner
              Pass (OPPP) is the key. Members keep the familiar app; ownership lives on-chain.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {ONE_WORLD_START.map((item, index) => {
              const Icon = item.icon;
              const ctaClass =
                'inline-flex items-center text-sm font-medium text-primary hover:text-primary-600 transition-colors';
              const ctaInner = (
                <>
                  {item.ctaLabel}
                  <ExternalLink size={14} className="ml-1.5" />
                </>
              );
              return (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  className={`${SF_UI.surfaceCard} ${SF_UI.surfaceCardPad} flex flex-col`}
                >
                  <div className="mb-3 flex items-center gap-3">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                      {item.step}
                    </span>
                    <Icon size={20} className="text-primary" aria-hidden />
                    <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="mb-4 flex-1 text-sm leading-relaxed text-gray-600">{item.body}</p>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={ctaClass}
                    >
                      {ctaInner}
                    </a>
                  ) : (
                    <Link to={item.href} className={ctaClass}>
                      {item.ctaLabel}
                      <ArrowUpRight size={14} className="ml-1.5" />
                    </Link>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Worlds grid */}
      <Section className="pt-10 pb-8" bgColor="bg-slate-50">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Explore worlds</h2>
            <p className="mt-1 text-gray-600">
              Each world is a sport home: <span className="font-medium text-gray-800">One</span>
              <span className="font-medium text-primary">SportsName</span>. Enter a live world to
              open its app.
            </p>
          </div>

          {/* Category chips */}
          <div
            className="flex flex-wrap gap-2"
            role="tablist"
            aria-label="Filter worlds by category"
          >
            {CATEGORY_FILTERS.map((f) => {
              const active = category === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCategory(f.id)}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all ${
                    active
                      ? 'bg-primary text-white shadow-sm shadow-primary/25'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-primary/40 hover:text-primary'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="popLayout">
          {filtered.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center"
            >
              <Globe2 className="mx-auto mb-3 text-gray-300" size={40} />
              <p className="text-lg font-semibold text-gray-700">No worlds match that search</p>
              <p className="mt-1 text-sm text-gray-500">
                Try another sport name, or clear filters to see all worlds.
              </p>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setCategory('all');
                }}
                className="mt-4 text-sm font-medium text-primary hover:underline"
              >
                Clear search & filters
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              layout
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
            >
              {filtered.map((world, index) => (
                <WorldCard key={world.id} world={world} index={index} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </Section>

      {/* Support strip — human help for the live world */}
      <Section bgColor="bg-white" className="pt-4 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="relative mx-auto max-w-4xl overflow-hidden rounded-2xl border border-primary/15 bg-gradient-to-br from-primary-50 via-white to-secondary-50 p-6 md:p-8 shadow-sm"
        >
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-lg">
              <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
                Human support
              </p>
              <h3 className="text-xl font-bold text-gray-900 md:text-2xl">
                Need a guide into OneTaekwondo?
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                The OneTaekwondo app is under construction. Message the host on WhatsApp for human
                support, or open the live world directly.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <a
                href={ONE_TAEKWONDO_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={SF_UI.ctaPrimary}
              >
                Open OneTaekwondo
                <ExternalLink size={16} className="ml-2" />
              </a>
              <a
                href={ONE_WORLD_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={SF_UI.ctaSecondary}
              >
                <MessageCircle size={16} className="mr-2" />
                Need human support click here
              </a>
            </div>
          </div>
        </motion.div>
      </Section>
    </>
  );
};

export default OneWorldPage;
